import { useCallback, useMemo } from "react";

import useTypedFlags from "hooks/useTypedFlags";

import useNewStackAvailableSpaces from "./useAvailableSpaces";
import { decodeUrlSearchParam } from "./utils";
import { StackCreationVendor, StackCreationWizardStep, StackFormFields } from "./types";
import { INITIAL_STACK_VALUES_SCHEMA } from "./schema";

const useValuesTransformer = () => {
  const availableSpaces = useNewStackAvailableSpaces();

  return useCallback(
    (values: StackFormFields): StackFormFields => ({
      ...values,
      [StackCreationWizardStep.Details]: {
        ...values[StackCreationWizardStep.Details],
        space:
          availableSpaces.find((s) => s.id === values[StackCreationWizardStep.Details].space)?.id ||
          availableSpaces[0]?.id,
      },
    }),
    [availableSpaces]
  );
};

const useInitialValues = (urlSearchParams: URLSearchParams): StackFormFields => {
  const { opentofuBackend } = useTypedFlags();
  const transformValues = useValuesTransformer();

  const initialValues: StackFormFields = useMemo(() => {
    const searchParamsRecord = decodeUrlSearchParam(urlSearchParams);
    const parsedInputValues = INITIAL_STACK_VALUES_SCHEMA.parse(searchParamsRecord);

    const vendorFromUrl = searchParamsRecord[StackCreationWizardStep.Vendor];
    const hasVendorInUrl =
      typeof vendorFromUrl === "object" && vendorFromUrl !== null && "vendor" in vendorFromUrl;

    if (opentofuBackend && !hasVendorInUrl) {
      parsedInputValues[StackCreationWizardStep.Vendor].vendor = StackCreationVendor.OpenTofu;
    }

    const transformedValues = transformValues(parsedInputValues);

    return transformedValues;
  }, [urlSearchParams, transformValues, opentofuBackend]);

  return initialValues;
};

export default useInitialValues;
